import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const formatApiError = (detail, fallback) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((error) => {
      const field = error.loc?.[error.loc.length - 1];
      return `${field ? `${field}: ` : ''}${error.msg || 'Invalid value'}`;
    }).join('; ');
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }
  return fallback;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Fetch current user info
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      // If token is invalid, clear it
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${backendUrl}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      // Store token in localStorage
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userData };
    } catch (error) {
      const errorMessage = formatApiError(error.response?.data?.detail, 'Login failed');
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${backendUrl}/api/auth/register`, userData);
      
      const { access_token, user: newUser } = response.data;
      
      // Store token in localStorage
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: newUser };
    } catch (error) {
      const errorMessage = formatApiError(error.response?.data?.detail, 'Registration failed');
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${backendUrl}/api/user/profile`, profileData);
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      const errorMessage = formatApiError(error.response?.data?.detail, 'Profile update failed');
      return { success: false, error: errorMessage };
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await axios.post(`${backendUrl}/api/user/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      });
      setUser(currentUser => currentUser ? { ...currentUser, must_change_password: false } : currentUser);
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiError(error.response?.data?.detail, 'Password change failed');
      return { success: false, error: errorMessage };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${backendUrl}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiError(error.response?.data?.detail, 'Password reset request failed');
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${backendUrl}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiError(error.response?.data?.detail, 'Password reset failed');
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
